import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sparkles, Rocket, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { RoadmapIcon } from "../lib/roadmapIcons";

export default function RoadmapTab() {
  const [roadmap, setRoadmap] = useState({ coming_soon: [], just_launched: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "important" });

  const fetchRoadmap = async () => {
    try {
      const rm = await apiClient.get("/roadmap/items");
      setRoadmap(rm.data || { coming_soon: [], just_launched: [] });
    } catch {
      // ignore first-load races
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoadmap(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const res = await apiClient.post("/feature-requests", form);
      setForm({ title: "", description: "", priority: "important" });
      toast.success(
        res.data?.message ||
        "Thanks! We've received your idea and will consider it for a future release."
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="roadmap-tab">
      {roadmap.just_launched.length > 0 && (
        <Card data-testid="just-launched-section" className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <PartyPopper className="w-5 h-5 text-emerald-600" />
              Just Launched
            </CardTitle>
            <p className="text-sm text-slate-500">New features you can use today.</p>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {roadmap.just_launched.map((item) => (
              <div
                key={item.roadmap_item_id}
                data-testid={`roadmap-launched-${item.roadmap_item_id}`}
                className="flex gap-3 p-4 rounded-lg border border-emerald-200 bg-white"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 border border-emerald-200 shrink-0">
                  <RoadmapIcon iconKey={item.icon} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-[#0A192F]">{item.title}</h4>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700 border border-emerald-500/40">
                      New
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 leading-snug">{item.tagline}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {(loading || roadmap.coming_soon.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Coming Soon
            </CardTitle>
            <p className="text-sm text-slate-500">What we&apos;re actively working on next.</p>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {loading ? (
              [1, 2].map((i) => <div key={i} className="h-20 bg-slate-100 rounded animate-pulse" />)
            ) : (
              roadmap.coming_soon.map((item) => (
                <div
                  key={item.roadmap_item_id}
                  data-testid={`roadmap-coming-soon-${item.roadmap_item_id}`}
                  className="flex gap-3 p-4 rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50 to-white"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-amber-200 shrink-0">
                    <RoadmapIcon iconKey={item.icon} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-[#0A192F]">{item.title}</h4>
                      {item.eta && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-700 border border-amber-500/30">
                          {item.eta}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 leading-snug">{item.tagline}</p>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Rocket className="w-5 h-5 text-[#0D9488]" />
            Request a Feature
          </CardTitle>
          <p className="text-sm text-slate-500">
            Tell us what you&apos;d love to see next. We read every submission.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4" data-testid="feature-request-form">
            <div>
              <Label htmlFor="fr-title">Title</Label>
              <Input
                id="fr-title"
                data-testid="feature-request-title"
                maxLength={140}
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Bulk-approve time entries by pay period"
              />
            </div>
            <div>
              <Label htmlFor="fr-desc">Description <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Textarea
                id="fr-desc"
                data-testid="feature-request-description"
                maxLength={2000}
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What's the problem, and how would this help your workflow?"
              />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="feature-request-priority" className="w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nice_to_have">Nice to have</SelectItem>
                  <SelectItem value="important">Important</SelectItem>
                  <SelectItem value="must_have">Must have</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                data-testid="feature-request-submit"
                disabled={saving || !form.title.trim()}
                className="bg-[#0D9488] hover:bg-[#14B8A6]"
              >
                {saving ? "Submitting..." : "Submit idea"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
